import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";

export interface IMultiTextInputConfig {
    // values that are suggested next to the new text typed by the user, e.g. the ones already used elsewhere
    suggestions: { (): Promise<Array<string>> };
}

// Free text multi select: the user picks from the suggestions or types a new value.
// Values are compared ignoring case and leading / trailing spaces, the first spelling wins.
export class MultiTextInput {
    private $element: JQuery;
    private $input: JQuery;
    private $search: JQuery;

    private values = new Array<string>();
    private suggestions = new Array<string>();

    private closeDelay?: number;
    private mouseDownOnSearch = false;

    private config: IMultiTextInputConfig;

    public onChange?: { (value: Array<string>) };

    constructor(config: IMultiTextInputConfig, $input?: JQuery) {
        this.config = config;
        this.$element = $('<div class="entityinput multitextinput" />');

        const placeholder = i18n("general.searchTerm");
        if ($input === undefined) {
            this.$input = $('<input type="text" class="form-control" />').attr("placeholder", placeholder);
            this.$input.appendTo(this.$element);
        } else {
            const parent = $input.attr("placeholder", placeholder).parent();
            const indexOfInput = $input.index();
            this.$input = $input.detach().appendTo(this.$element);

            if (indexOfInput === 0) this.$element.prependTo(parent);
            else this.renderTo(parent);
        }

        this.$search = $('<div class="search" />').appendTo(this.$element);

        this.$input
            .focus(() => this.openSearch())
            .blur(() => {
                if (this.mouseDownOnSearch) return;

                // a typed text that was not confirmed yet must not get lost when the form is saved
                this.addTypedText();
                this.closeSearch();
            })
            .keydown((e) => {
                if (e.key !== "Enter") return;

                // Enter confirms the typed text, it must not submit the form
                e.preventDefault();
                e.stopPropagation();
                this.addTypedText();
            })
            .keyup((e) => {
                if (e.key === "Enter") return;

                this.renderSearch();
            });

        this.$search
            .mousedown(() => {
                this.mouseDownOnSearch = true;
            })
            .mouseup(() => {
                this.mouseDownOnSearch = false;
            })
            .mouseleave(() => {
                this.mouseDownOnSearch = false;
            });
    }

    public getValue(): Array<string> {
        return [...this.values];
    }

    public setValue(values?: Array<string>): MultiTextInput {
        this.values = [];
        this.$element.find("> .link").remove();

        for (const value of values ?? []) {
            this.add(value, false);
        }

        this.notifyChange();
        return this;
    }

    public setError(hasError: boolean): MultiTextInput {
        if (hasError) this.$element.addClass("is-invalid");
        else this.$element.removeClass("is-invalid");

        return this;
    }

    public setDisabled(isDisabled: boolean): MultiTextInput {
        this.$input.prop("disabled", isDisabled);
        this.$element.toggleClass("disabled", isDisabled);

        return this;
    }

    public renderTo($element: JQuery): MultiTextInput {
        this.$element.appendTo($element);
        return this;
    }

    public static createFromInput($input: JQuery, config: IMultiTextInputConfig): MultiTextInput {
        return new MultiTextInput(config, $input);
    }

    private static normalize(value: string): string {
        return value.trim().toLowerCase();
    }

    private isSelected(value: string): boolean {
        const normalized = MultiTextInput.normalize(value);
        return this.values.some((v) => MultiTextInput.normalize(v) === normalized);
    }

    private addTypedText() {
        const typed = (this.$input.val() as string).trim();
        this.$input.val("");

        if (typed.length > 0) this.add(typed, true);

        this.renderSearch();
    }

    private add(value: string, notify: boolean) {
        const trimmed = value.trim();
        if (trimmed.length === 0 || this.isSelected(trimmed)) return;

        this.values.push(trimmed);

        const link = $('<div class="link"></div>').text(trimmed).data("value", trimmed).insertBefore(this.$input);
        $('<div class="remover"><i class="flaticon2-cross"></i></div>')
            .appendTo(link)
            .click(() => {
                const idx = this.values.indexOf(trimmed);
                if (idx !== -1) this.values.splice(idx, 1);

                link.remove();
                this.notifyChange();
                this.renderSearch();
            });

        if (notify) this.notifyChange();
    }

    private openSearch() {
        if (this.closeDelay !== undefined) {
            clearTimeout(this.closeDelay);
            this.closeDelay = undefined;
        }

        this.$search
            .stop(true, true)
            .css("top", this.$element.height()! + 3)
            .slideDown("fast", "easeOutExpo");
        this.$element.addClass("open");

        this.$search
            .empty()
            .append(
                '<div class="loading"><div class="kt-spinner kt-spinner--sm kt-spinner--success"></div> ' +
                    i18n("general.searchingResults") +
                    "</div>"
            );

        this.config
            .suggestions()
            .then((suggestions) => {
                this.suggestions = suggestions;
                this.renderSearch();
            })
            .catch((ex) => {
                logError(ex);
                this.suggestions = [];
                this.renderSearch();
            });
    }

    private closeSearch() {
        if (this.closeDelay !== undefined) clearTimeout(this.closeDelay);

        this.closeDelay = window.setTimeout(() => {
            this.$input.val("");
            this.$search.stop(true, false).slideUp("fast", "easeOutExpo", () => {
                this.$search.removeAttr("style");
            });
            this.$element.removeClass("open");
            this.closeDelay = undefined;
        }, 120);
    }

    private renderSearch() {
        const typed = (this.$input.val() as string).trim();
        const normalizedTyped = MultiTextInput.normalize(typed);

        // suggested values come from other items, so the same value may be suggested in different spellings
        const seen = new Set<string>();
        const options = this.suggestions.filter((s) => {
            const normalized = MultiTextInput.normalize(s);
            if (normalized.length === 0 || seen.has(normalized)) return false;
            seen.add(normalized);

            return !this.isSelected(s) && normalized.includes(normalizedTyped);
        });

        this.$search.empty();

        if (typed.length > 0 && !this.isSelected(typed) && !seen.has(normalizedTyped)) {
            this.appendOption(i18n("general.addNewValue", { value: typed }), typed);
        }

        for (const option of options) {
            this.appendOption(option, option);
        }

        if (this.$search.children().length === 0) {
            this.$search.append('<div class="nodata">' + i18n("general.noSearchResults") + "</div>");
        }
    }

    private appendOption(text: string, value: string) {
        $('<div class="option"></div>')
            .text(text)
            .appendTo(this.$search)
            .click((e) => {
                e.preventDefault();
                e.stopPropagation();

                this.add(value, true);
                this.$input.val("");
                this.mouseDownOnSearch = false;
                this.$input.focus();
                this.renderSearch();
            });
    }

    private notifyChange() {
        if (this.onChange) this.onChange(this.getValue());
    }
}
